import { useState, useEffect, useCallback  } from 'react'
import { useTranslation } from 'react-i18next'
import MessageBox from './MessageBox'
import { withBase } from '../config/api'
import Overlay from './Overlay'
import VOPModal from './VOPModal'
import CountryInfoModal from './CountryInfoModal'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import showPwd from '../assets/images/icons/show_pwd.png'
import hidePwd from '../assets/images/icons/hide_pwd.png'

import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const LABEL_W = 200
const INPUT_W = 250

const Row = ({ htmlFor, label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <label htmlFor={htmlFor} style={{ width: LABEL_W, fontWeight: 700 }}>
      {label}
    </label>
    {children}
  </div>
)

const RegistrationModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation()
  const [message, setMessage] = useState(null)
  const [loadingRef, setLoadingRef] = useState(false)
  const [openVOP, setOpenVOP] = useState(false)
  const [countryInfoOpen, setCountryInfoOpen] = useState(false)
  const [vopLocked, setVopLocked] = useState(false)
  const [vopOpenedFrom, setVopOpenedFrom] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [countries, setCountries] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(false)

  const [form, setForm] = useState({
    account_type: 'individual',
    company_name: '', company_ico: '',
    company_dic: '', company_ic_dph: '',
    meno: '', priezvisko: '', email: '', telefon: '',
    ulica: '', mesto: '', psc: '', krajina: '',
    iban: '', swift: '', banka: '',
    referral_code: '', parent_referral_code: '',
    password: '', confirmPassword: '',
    vop_suhlas: false
  })

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return

      if (openVOP || countryInfoOpen) {
        e.preventDefault()
        return
      }

      onClose()
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, openVOP, countryInfoOpen, onClose])

  const updateForm = (k, v) =>
    setForm(prev => ({ ...prev, [k]: v }))

  const clearForm = useCallback(() =>
    setForm({
      account_type: 'individual',
      company_name: '', company_ico: '',
      company_dic: '', company_ic_dph: '',
      meno: '', priezvisko: '', email: '', telefon: '',
      ulica: '', mesto: '', psc: '', krajina: '',
      iban: '', swift: '', banka: '',
      referral_code: '', parent_referral_code: '',
      password: '', confirmPassword: '',
      vop_suhlas: vopLocked ? true : false
    }),
  [vopLocked])

  useEffect(() => {
    if (!open) {
      setMessage(null)
      setLoadingRef(false)
      setOpenVOP(false)
      setCountryInfoOpen(false)
      setVopLocked(false)
      setVopOpenedFrom(null)
      setHoverBtn(null)
      setPressedBtn(null)
      clearForm()
    }
  }, [open, clearForm])

  const fieldLabel = (field) => {
    const map = {
      FIELD_MENO: t('FIRST_NAME'),
      FIELD_PRIEZVISKO: t('LAST_NAME'),
      FIELD_EMAIL: t('EMAIL'),
      FIELD_PASSWORD: t('PASSWORD'),
      FIELD_TELEFON: t('PHONE'),
      FIELD_ULICA: t('STREET'),
      FIELD_MESTO: t('CITY'),
      FIELD_PSC: t('ZIP'),
      FIELD_KRAJINA: t('COUNTRY'),
      FIELD_REFERRAL_CODE: t('REFERRAL_CODE')
    }

    return map[field] || field
  }

  const fromServerMessage = (data) => {
    if (!data) return t('SERVER-ERROR')

    if (data.fields && Array.isArray(data.fields) && data.fields.length > 0) {
      const fields = data.fields.map(f => fieldLabel(f)).join(', ')
      return `${t('MISSING-DATA')}: ${fields}`
    }

    return (data?.message || '').trim() || t('SERVER-ERROR')
  }

  const requestReferral = async () => {
    try {
      setLoadingRef(true)

      let sessionToken = localStorage.getItem('refSession')
      if (!sessionToken) {
        sessionToken = crypto.randomUUID()
        localStorage.setItem('refSession', sessionToken)
      }

      const res = await fetch(
        withBase(`/referral/request?session_token=${sessionToken}`),
        { headers: { 'Accept-Language': i18n.language } }
      )

      const data = await res.json().catch(() => null)

      if (res.ok && data?.referral_code) {
        updateForm('referral_code', data.referral_code)

        setMessage({
          message: t('REFERRAL_CODE_ASSIGNED') || 'Referral kód bol pridelený',
          type: 'success'
        })
      } else {
        setMessage({
          message: fromServerMessage(data),
          type: 'error'
        })
      }
    } catch {
      setMessage({
        message: t('SERVER-ERROR'),
        type: 'error'
      })
    } finally {
      setLoadingRef(false)
    }
  }

  useEffect(() => {
    if (!open) return

    const loadCountries = async () => {
      try {
        setCountriesLoading(true)

        const res = await fetch(withBase('/countries'))
        const data = await res.json()

        if (!res.ok || !Array.isArray(data)) {
          setCountries([])
          return
        }

        setCountries(data)
      } catch {
        setCountries([])
      } finally {
        setCountriesLoading(false)
      }
    }

    loadCountries()
  }, [open])

  const register = async () => {
    const missingFields = []

    if (!form.meno.trim()) missingFields.push(t('FIRST_NAME'))
    if (!form.priezvisko.trim()) missingFields.push(t('LAST_NAME'))
    if (!form.email.trim()) missingFields.push(t('EMAIL'))
    if (!form.password) missingFields.push(t('PASSWORD'))
    if (!form.confirmPassword) missingFields.push(t('CONFIRM_PASSWORD'))
    if (!form.telefon.trim()) missingFields.push(t('PHONE'))
    if (!form.krajina) missingFields.push(t('COUNTRY'))

if (form.account_type === 'company') {
  if (!form.company_name.trim()) {
    missingFields.push(t('REG_COMPANY_NAME'))
  }
  if (!form.company_ico.trim()) {
    missingFields.push(t('REG_COMPANY_ICO'))
  }
}

    if (missingFields.length > 0) {
      setMessage({
        message: `${t('MISSING-DATA')}: ${missingFields.join(', ')}`,
        type: 'error'
      })
      return
    }

    if (form.password !== form.confirmPassword) {
      setMessage({
        message: t('PASSWORD-MISMATCH'),
        type: 'error'
      })
      return
    }

    if (!form.vop_suhlas) {
      setMessage({
        message: t('VOP-REQUIRED'),
        type: 'error'
      })
      return
    }

    try {
      setLoadingRef(true)

      const res = await fetch(withBase('/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language
        },
        body: JSON.stringify({
          account_type: form.account_type,
          company_name: form.account_type === 'company' ? form.company_name.trim() : null,
          company_ico: form.account_type === 'company' ? form.company_ico.trim() : null,
          company_dic: form.account_type === 'company' ? form.company_dic.trim() || null : null,
          company_ic_dph: form.account_type === 'company' ? form.company_ic_dph.trim() || null : null,
          email: form.email,
          meno: form.meno,
          priezvisko: form.priezvisko,
          telefon: form.telefon,
          ulica: form.ulica,
          mesto: form.mesto,
          psc: form.psc,
          krajina: form.krajina,
          iban: form.iban,
          swift: form.swift,
          banka: form.banka,
          password: form.password,
          confirmPassword: form.confirmPassword,
          referral_code: form.referral_code,
          vop_suhlas: form.vop_suhlas
        })
      })

      const data = await res.json().catch(() => null)

      setMessage({
        message: fromServerMessage(data),
        type: res.ok ? 'success' : 'error'
      })

      if (res.ok) clearForm()
    } catch {
      setMessage({
        message: t('SERVER-ERROR'),
        type: 'error'
      })
    } finally {
      setLoadingRef(false)
    }
  }

  if (!open) {
    if (vopLocked) setVopLocked(false)
    if (form.vop_suhlas) updateForm('vop_suhlas', false)
    return null
  }

  return (
    <>
      <Overlay
        open={loadingRef}
        zIndex={4000}
        backdropOpacity={0.75}
      />

      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3000
        }}
      >
        <div
          style={{
            background: '#ededed',
	    textAlign: 'left',
            padding: 20,
            border: '2px solid #107c6b',
            borderRadius: 12,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            maxHeight: '90vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: LABEL_W + INPUT_W + 32
          }}
          onKeyDown={e => {
            if (e.key !== 'Enter') return
            if (e.target.tagName === 'BUTTON') return
            register()
          }}
        >
          <h3
            style={{
              fontSize: 28,
              margin: 0,
              marginBottom: 24
            }}
          >
            {t('REGISTRATION')}
          </h3>

          {message && (
            <MessageBox
              message={message.message}
              type={message.type}
            />
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}
          >
            <Row
              htmlFor="referral_code"
              label={t('REFERRAL_CODE')}
            >
              <input
                id="referral_code"
                value={form.referral_code}
                onChange={e =>
                  updateForm('referral_code', e.target.value)
                }
                style={{ width: 145 }}
              />

              <button
                style={{
                  marginLeft: 6,
                  background:
                    hoverBtn === 'referral'
                      ? '#06857a'
                      : '#1e3f44',
                  color: '#fff',
                  border: 'none',
                  padding: '4px 6px',
                  borderRadius: 10,
                  fontSize: 14,
                  cursor: 'pointer',
                  boxShadow:
                    pressedBtn === 'referral'
                      ? 'inset 0 2px 4px rgba(0,0,0,0.4)'
                      : '0 3px 6px rgba(0,0,0,0.4)',
                  transform:
                    pressedBtn === 'referral'
                      ? 'scale(0.96)'
                      : 'scale(1)',
                  transition:
                    'transform 0.08s ease, box-shadow 0.08s ease'
                }}
                onMouseEnter={() => setHoverBtn('referral')}
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() => setPressedBtn('referral')}
                onMouseUp={() => setPressedBtn(null)}
                onClick={requestReferral}
                disabled={loadingRef}
              >
                {t('REQUEST_CODE') || 'Vyžiadať kód'}
              </button>
            </Row>

            <Row
              htmlFor="account_type"
              label={t('REG_ACCOUNT_TYPE')}
            >
              <select
                id="account_type"
                value={form.account_type}
                onChange={e =>
                  updateForm('account_type', e.target.value)
                }
		style={{
		  width: INPUT_W + 6,
		  height: 20,
		  boxSizing: 'content-box'
		}}
              >
                <option value="individual">
                  {t('REG_INDIVIDUAL')}
                </option>
                <option value="company">
                  {t('REG_COMPANY')}
                </option>
              </select>
            </Row>

            {form.account_type === 'company' && (
              <>
                <Row
                  htmlFor="company_name"
                  label={t('REG_COMPANY_NAME')}
                >
                  <input
                    id="company_name"
                    value={form.company_name}
                    onChange={e =>
                      updateForm('company_name', e.target.value)
                    }
                    style={{ width: INPUT_W }}
                  />
                </Row>

                <Row
                  htmlFor="company_ico"
                  label={t('REG_COMPANY_ICO')}
                >
                  <input
                    id="company_ico"
                    value={form.company_ico}
                    onChange={e =>
                      updateForm('company_ico', e.target.value)
                    }
                    style={{ width: INPUT_W }}
                  />
                </Row>

                <Row
                  htmlFor="company_dic"
                  label={t('REG_COMPANY_DIC')}
                >
                  <input
                    id="company_dic"
                    value={form.company_dic}
                    onChange={e =>
                      updateForm('company_dic', e.target.value)
                    }
                    style={{ width: INPUT_W }}
                  />
                </Row>

                <Row
                  htmlFor="company_ic_dph"
                  label={t('REG_COMPANY_VAT_ID')}
                >
                  <input
                    id="company_ic_dph"
                    value={form.company_ic_dph}
                    onChange={e =>
                      updateForm('company_ic_dph', e.target.value)
                    }
                    style={{ width: INPUT_W }}
                  />
                </Row>
              </>
            )}

		<Row htmlFor="meno" label={t('FIRST_NAME')}>
 		 <input
		    id="meno"
		    value={form.meno}
		    onChange={e =>
		      updateForm('meno', e.target.value)
		    }
 		   placeholder={
 		     form.account_type === 'company'
		        ? t('REG_CONTACT_PERSON')
 		       : ''
 		   }
 		   style={{ width: INPUT_W }}
 		 />
		</Row>

            <Row
              htmlFor="priezvisko"
              label={t('LAST_NAME')}
            >
              <input
                id="priezvisko"
                value={form.priezvisko}
                onChange={e =>
                  updateForm('priezvisko', e.target.value)
                }
                style={{ width: INPUT_W }}
              />
            </Row>

            <Row htmlFor="email" label={t('EMAIL')}>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={e =>
                  updateForm('email', e.target.value)
                }
                style={{ width: INPUT_W }}
              />
            </Row>

            <Row htmlFor="password" label={t('PASSWORD')}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e =>
                  updateForm('password', e.target.value)
                }
                onClick={(e) => {
                  const rect = e.target.getBoundingClientRect()

                  if (e.clientX > rect.right - 40) {
                    setShowPassword(prev => !prev)
                  }
                }}
                onMouseMove={(e) => {
                  const rect = e.target.getBoundingClientRect()

                  e.target.style.cursor =
                    e.clientX > rect.right - 40
                      ? 'pointer'
                      : 'text'
                }}
                style={{
                  width: INPUT_W,
                  backgroundImage: `url(${showPassword ? hidePwd : showPwd})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '18px'
                }}
              />
            </Row>

            <Row
              htmlFor="confirmPassword"
              label={t('CONFIRM_PASSWORD')}
            >
              <input
                id="confirmPassword"
                type={
                  showConfirmPassword
                    ? 'text'
                    : 'password'
                }
                value={form.confirmPassword}
                onChange={e =>
                  updateForm(
                    'confirmPassword',
                    e.target.value
                  )
                }
                onClick={(e) => {
                  const rect =
                    e.target.getBoundingClientRect()

                  if (e.clientX > rect.right - 40) {
                    setShowConfirmPassword(prev => !prev)
                  }
                }}
                onMouseMove={(e) => {
                  const rect =
                    e.target.getBoundingClientRect()

                  e.target.style.cursor =
                    e.clientX > rect.right - 40
                      ? 'pointer'
                      : 'text'
                }}
                style={{
                  width: INPUT_W,
                  backgroundImage: `url(${showConfirmPassword ? hidePwd : showPwd})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '18px'
                }}
              />
            </Row>

            <Row htmlFor="telefon" label={t('PHONE')}>
              <input
                id="telefon"
                value={form.telefon}
                onChange={e =>
                  updateForm('telefon', e.target.value)
                }
                style={{ width: INPUT_W }}
              />
            </Row>

            <Row htmlFor="ulica" label={t('STREET')}>
              <input
                id="ulica"
                value={form.ulica}
                onChange={e =>
                  updateForm('ulica', e.target.value)
                }
                style={{ width: INPUT_W }}
              />
            </Row>

            <Row htmlFor="mesto" label={t('CITY')}>
              <input
                id="mesto"
                value={form.mesto}
                onChange={e =>
                  updateForm('mesto', e.target.value)
                }
                style={{ width: INPUT_W }}
              />
            </Row>

            <Row htmlFor="psc" label={t('ZIP')}>
              <input
                id="psc"
                value={form.psc}
                onChange={e =>
                  updateForm('psc', e.target.value)
                }
                style={{ width: INPUT_W }}
              />
            </Row>

            <Row
              htmlFor="krajina"
              label={t('COUNTRY')}
            >
              <select
                id="krajina"
                value={form.krajina}
                onChange={e =>
                  updateForm('krajina', e.target.value)
                }
                disabled={countriesLoading}
                style={{
                  width: 225,
                  height: 22
                }}
              >
                <option value=""></option>

                {countries.map(country => (
                  <option
                    key={country.code}
                    value={country.code}
                  >
                    {country.name}
                  </option>
                ))}
              </select>

              <button
                type="button"
                style={{
                  ...modalPrimaryButtonStyle(
                    pressedBtn,
                    'country-info',
                    hoverBtn === 'country-info'
                  ),
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  padding: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={() =>
                  setHoverBtn('country-info')
                }
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() =>
                  setPressedBtn('country-info')
                }
                onMouseUp={() => setPressedBtn(null)}
                onClick={() => setCountryInfoOpen(true)}
              >
                <InfoOutlinedIcon
                  style={{ fontSize: 15 }}
                />
              </button>
            </Row>

            <Row htmlFor="iban" label="IBAN">
              <input
                id="iban"
                value={form.iban}
                onChange={e =>
                  updateForm('iban', e.target.value)
                }
                style={{ width: INPUT_W }}
              />
            </Row>

            <Row htmlFor="swift" label="SWIFT">
              <input
                id="swift"
                value={form.swift}
                onChange={e =>
                  updateForm('swift', e.target.value)
                }
                style={{ width: INPUT_W }}
              />
            </Row>

            <Row htmlFor="banka" label={t('BANK')}>
              <input
                id="banka"
                value={form.banka}
                onChange={e =>
                  updateForm('banka', e.target.value)
                }
                style={{ width: INPUT_W }}
              />
            </Row>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <div
                onClick={() => {
                  setVopOpenedFrom('link')
                  setOpenVOP(true)
                }}
                style={{
                  width: LABEL_W,
                  fontWeight: 700,
                  cursor: 'pointer',
                  color: '#107c6b',
                  textDecoration: 'underline'
                }}
              >
                {t('AGREE_VOP')}
              </div>

              <input
                type="checkbox"
                checked={form.vop_suhlas}
                disabled={vopLocked}
                onChange={e => {
                  if (!e.target.checked) return

                  updateForm('vop_suhlas', true)
                  setVopLocked(true)
                  setVopOpenedFrom('checkbox')
                  setOpenVOP(true)
                }}
              />
            </div>

            <div
              style={{
                display: 'flex',
                gap: 8,
                marginTop: 8,
                justifyContent: 'center'
              }}
            >
              <button
                style={modalPrimaryButtonStyle(
                  pressedBtn,
                  'register',
                  hoverBtn === 'register'
                )}
                onMouseEnter={() =>
                  setHoverBtn('register')
                }
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() =>
                  setPressedBtn('register')
                }
                onMouseUp={() => setPressedBtn(null)}
                onClick={register}
              >
                {t('REGISTER')}
              </button>

              <button
                style={modalSecondaryButtonStyle(
                  pressedBtn,
                  'clear',
                  hoverBtn === 'clear'
                )}
                onMouseEnter={() =>
                  setHoverBtn('clear')
                }
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() =>
                  setPressedBtn('clear')
                }
                onMouseUp={() => setPressedBtn(null)}
                onClick={clearForm}
              >
                {t('CLEAR')}
              </button>

              <button
                style={modalSecondaryButtonStyle(
                  pressedBtn,
                  'close',
                  hoverBtn === 'close'
                )}
                onMouseEnter={() =>
                  setHoverBtn('close')
                }
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() =>
                  setPressedBtn('close')
                }
                onMouseUp={() => setPressedBtn(null)}
                onClick={onClose}
              >
                {t('CLOSE')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <VOPModal
        open={openVOP}
        onClose={() => setOpenVOP(false)}
        openedFrom={vopOpenedFrom}
      />

      <CountryInfoModal
        open={countryInfoOpen}
        onClose={() => setCountryInfoOpen(false)}
      />
    </>
  )
}

export default RegistrationModal